import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Eye, ArrowRight, FileText, TrendingUp, Calendar, Leaf, 
  Brain, Heart, Zap, Activity, Smile, Star, Clock, 
  CheckCircle, Search, Filter, LayoutGrid, List,
  User, ClipboardList, BarChart3, Lock, Sparkles
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, getAuthHeader, isAuthenticated } = useAuth();
  
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tests'); // 'tests', 'results', 'profile'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [hasAccess, setHasAccess] = useState(false);
  const [completedTests, setCompletedTests] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch tests
        const testsRes = await axios.get(`${API}/tests?language=${language}`);
        setTests(testsRes.data);

        // Fetch user data and results
        const userRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
        if (userRes.data.package_status === 'active' || userRes.data.is_premium) {
          setHasAccess(true);
        }

        const resultsRes = await axios.get(`${API}/results`, { headers: getAuthHeader() });
        setResults(resultsRes.data);
        setCompletedTests(resultsRes.data.map(r => r.test_id));
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          navigate('/auth');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, navigate, getAuthHeader, language]);

  const categories = [
    { id: 'all', label: language === 'it' ? 'Tutti' : 'All', icon: LayoutGrid },
    { id: 'depression', label: language === 'it' ? 'Depressione' : 'Depression', icon: Brain },
    { id: 'anxiety', label: language === 'it' ? 'Ansia' : 'Anxiety', icon: Heart },
    { id: 'stress', label: 'Stress', icon: Zap },
    { id: 'comprehensive', label: language === 'it' ? 'Completi' : 'Comprehensive', icon: Activity },
    { id: 'wellbeing', label: language === 'it' ? 'Benessere' : 'Wellbeing', icon: Smile },
  ];

  const categoryIcons = {
    depression: Brain,
    anxiety: Heart,
    stress: Zap,
    comprehensive: Activity,
    wellbeing: Smile,
    distress: Activity,
    'self-esteem': Star,
  };

  const categoryColors = {
    depression: 'bg-blue-500/10 text-blue-600',
    anxiety: 'bg-rose-500/10 text-rose-600',
    stress: 'bg-amber-500/10 text-amber-600',
    comprehensive: 'bg-purple-500/10 text-purple-600',
    wellbeing: 'bg-emerald-500/10 text-emerald-600',
    distress: 'bg-orange-500/10 text-orange-600',
    'self-esteem': 'bg-cyan-500/10 text-cyan-600',
  };

  // Filter tests
  const filteredTests = tests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         test.short_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         test.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Chart data
  const chartData = results
    .slice(0, 10)
    .reverse()
    .map(result => ({
      date: new Date(result.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
        month: 'short',
        day: 'numeric'
      }),
      score: result.total_score,
      test: result.test_id.toUpperCase()
    }));

  const tabs = [
    { id: 'tests', label: language === 'it' ? 'I Miei Test' : 'My Tests', icon: ClipboardList },
    { id: 'results', label: language === 'it' ? 'Risultati' : 'Results', icon: BarChart3 },
    { id: 'profile', label: language === 'it' ? 'Profilo' : 'Profile', icon: User },
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-b from-secondary/30 to-background" data-testid="dashboard-page">
      <div className="container-wide">
        {/* IMT App Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-[#2563eb] to-blue-500 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center">
                    <span className="text-2xl font-black">
                      <span className="text-[#2563eb]">I</span>
                      <span className="text-primary">M</span>
                      <span className="text-[#2563eb]">T</span>
                    </span>
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold text-lg">
                      {language === 'it' ? 'Prova la nuova App IMT' : 'Try the new IMT App'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {language === 'it' 
                        ? 'AI Companion, Mood Tracker e tutti i test in un\'unica app'
                        : 'AI Companion, Mood Tracker and all tests in one app'}
                    </p>
                  </div>
                </div>
                <Button
                  className="rounded-full bg-white text-[#2563eb] hover:bg-white/90 font-semibold"
                  onClick={() => navigate('/app')}
                >
                  {language === 'it' ? 'Apri App' : 'Open App'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Leaf className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">{t('dashboard.welcome')},</p>
                <h1 className="text-2xl sm:text-3xl font-serif font-bold text-primary" data-testid="dashboard-welcome">
                  {user?.name}
                </h1>
              </div>
            </div>
            
            {/* Access Badge */}
            {hasAccess ? (
              <Badge className="bg-green-100 text-green-700 px-4 py-2 self-start sm:self-auto">
                <CheckCircle className="w-4 h-4 mr-2" />
                {language === 'it' ? 'Accesso Completo Attivo' : 'Full Access Active'}
              </Badge>
            ) : (
              <Button
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 self-start sm:self-auto"
                onClick={() => navigate('/pricing')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {language === 'it' ? 'Sblocca Tutti i Test' : 'Unlock All Tests'}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex gap-2 mb-8 overflow-x-auto pb-2"
        >
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={`rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id ? 'bg-primary text-primary-foreground' : ''
              }`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          {/* Tests Tab */}
          {activeTab === 'tests' && (
            <motion.div
              key="tests"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Search and Filters */}
              <Card className="bg-white border-0 shadow-soft rounded-2xl mb-6">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={language === 'it' ? 'Cerca test...' : 'Search tests...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-full border-secondary"
                        data-testid="search-input"
                      />
                    </div>
                    
                    {/* View Toggle */}
                    <div className="flex gap-2">
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'outline'}
                        size="icon"
                        className="rounded-full"
                        onClick={() => setViewMode('grid')}
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="icon"
                        className="rounded-full"
                        onClick={() => setViewMode('list')}
                      >
                        <List className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Category Filters */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {categories.map((cat) => (
                      <Button
                        key={cat.id}
                        variant={selectedCategory === cat.id ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-full ${selectedCategory === cat.id ? '' : 'hover:bg-secondary'}`}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        <cat.icon className="w-3 h-3 mr-1" />
                        {cat.label}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tests Count */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-muted-foreground">
                  {filteredTests.length} {language === 'it' ? 'test disponibili' : 'tests available'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {completedTests.length} {language === 'it' ? 'completati' : 'completed'}
                </p>
              </div>

              {/* Tests Grid/List */}
              {!hasAccess ? (
                <Card className="bg-gradient-to-r from-accent/10 to-primary/10 border-2 border-accent/30 rounded-3xl">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-8 h-8 text-accent" />
                    </div>
                    <h2 className="text-xl font-serif font-bold text-primary mb-2">
                      {language === 'it' ? 'Sblocca i Test' : 'Unlock Tests'}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      {language === 'it' 
                        ? 'Acquista la Valutazione Completa per accedere a tutti i test psicologici.'
                        : 'Purchase the Complete Assessment to access all psychological tests.'}
                    </p>
                    <Button
                      size="lg"
                      className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                      onClick={() => navigate('/pricing')}
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      {language === 'it' ? 'Acquista - €199' : 'Buy - €199'}
                    </Button>
                  </CardContent>
                </Card>
              ) : viewMode === 'grid' ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTests.map((test, index) => {
                    const IconComponent = categoryIcons[test.category] || Brain;
                    const colorClass = categoryColors[test.category] || 'bg-primary/10 text-primary';
                    const isCompleted = completedTests.includes(test.id);
                    
                    return (
                      <motion.div
                        key={test.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.03 }}
                      >
                        <Card 
                          className="h-full bg-white border-0 shadow-soft rounded-2xl overflow-hidden group card-hover cursor-pointer"
                          onClick={() => navigate(`/test/${test.id}`)}
                          data-testid={`test-card-${test.id}`}
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass} group-hover:scale-110 transition-transform duration-300`}>
                                <IconComponent className="w-5 h-5" />
                              </div>
                              <div className="flex items-center gap-2">
                                {isCompleted && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {language === 'it' ? 'Fatto' : 'Done'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            <h3 className="font-semibold text-primary mb-1 line-clamp-1 group-hover:text-primary/80">
                              {test.short_name}
                            </h3>
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                              {test.description}
                            </p>
                            
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {test.estimated_time} min
                              </span>
                              <span>{test.question_count} {language === 'it' ? 'dom.' : 'q.'}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <Card className="bg-white border-0 shadow-soft rounded-2xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'it' ? 'Test' : 'Test'}</TableHead>
                        <TableHead className="hidden sm:table-cell">{language === 'it' ? 'Categoria' : 'Category'}</TableHead>
                        <TableHead>{language === 'it' ? 'Durata' : 'Duration'}</TableHead>
                        <TableHead>{language === 'it' ? 'Stato' : 'Status'}</TableHead>
                        <TableHead className="text-right">{language === 'it' ? 'Azione' : 'Action'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTests.map((test) => {
                        const isCompleted = completedTests.includes(test.id);
                        return (
                          <TableRow key={test.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => navigate(`/test/${test.id}`)}>
                            <TableCell>
                              <div>
                                <p className="font-semibold text-primary">{test.short_name}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{test.name}</p>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Badge variant="outline" className="capitalize">{test.category}</Badge>
                            </TableCell>
                            <TableCell>{test.estimated_time} min</TableCell>
                            <TableCell>
                              {isCompleted ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {language === 'it' ? 'Fatto' : 'Done'}
                                </Badge>
                              ) : (
                                <Badge variant="outline">{language === 'it' ? 'Da fare' : 'To do'}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" className="rounded-full">
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </motion.div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {results.length === 0 ? (
                <Card className="bg-white border-0 shadow-medium rounded-3xl text-center py-16">
                  <CardContent>
                    <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <h2 className="text-xl font-serif font-bold text-primary mb-2">
                      {t('results.noResults')}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      {language === 'it' 
                        ? 'Completa il tuo primo test per vedere i risultati qui.'
                        : 'Complete your first test to see results here.'}
                    </p>
                    <Button
                      className="rounded-full"
                      onClick={() => setActiveTab('tests')}
                    >
                      {language === 'it' ? 'Vai ai Test' : 'Go to Tests'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <Card className="bg-white border-0 shadow-soft rounded-2xl">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {language === 'it' ? 'Test Completati' : 'Tests Completed'}
                            </p>
                            <p className="text-2xl font-bold text-primary">{results.length}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-white border-0 shadow-soft rounded-2xl">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-accent" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {language === 'it' ? 'Ultimo Punteggio' : 'Last Score'}
                            </p>
                            <p className="text-2xl font-bold text-primary">{results[0]?.total_score || '-'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-white border-0 shadow-soft rounded-2xl">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {language === 'it' ? 'Ultimo Test' : 'Last Test'}
                            </p>
                            <p className="text-lg font-bold text-primary font-mono">
                              {results[0]?.test_id?.toUpperCase()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Chart */}
                  {chartData.length > 1 && (
                    <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden mb-8">
                      <CardHeader>
                        <CardTitle className="text-xl font-serif text-primary">
                          {language === 'it' ? 'Andamento nel Tempo' : 'Progress Over Time'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis dataKey="date" tick={{ fill: '#5C7566', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#5C7566', fontSize: 12 }} />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#fff', 
                                  border: 'none', 
                                  borderRadius: '12px',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="score" 
                                stroke="#2C4F3E" 
                                strokeWidth={3}
                                dot={{ fill: '#2C4F3E', strokeWidth: 2, r: 5 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Results Table */}
                  <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-xl font-serif text-primary">
                        {language === 'it' ? 'Storico Risultati' : 'Results History'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('dashboard.date')}</TableHead>
                            <TableHead>{t('dashboard.test')}</TableHead>
                            <TableHead>{t('dashboard.score')}</TableHead>
                            <TableHead className="text-right">{t('dashboard.actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.map((result, index) => (
                            <TableRow key={result.id}>
                              <TableCell>
                                {new Date(result.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </TableCell>
                              <TableCell>
                                <span className="font-mono bg-secondary px-2 py-1 rounded-full text-sm">
                                  {result.test_id.toUpperCase()}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="font-bold text-primary">{result.total_score}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-full hover:bg-primary/10"
                                  onClick={() => navigate(`/results/${result.id}`)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  {t('dashboard.view')}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              )}
            </motion.div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-white border-0 shadow-medium rounded-3xl">
                  <CardHeader>
                    <CardTitle className="text-xl font-serif text-primary">
                      {language === 'it' ? 'Informazioni Personali' : 'Personal Information'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{language === 'it' ? 'Nome' : 'Name'}</p>
                      <p className="font-semibold text-primary">{user?.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-semibold text-primary">{user?.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{language === 'it' ? 'Telefono' : 'Phone'}</p>
                      <p className="font-semibold text-primary">{user?.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{language === 'it' ? 'Data di nascita' : 'Birth Date'}</p>
                      <p className="font-semibold text-primary">{user?.birth_date || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-medium rounded-3xl">
                  <CardHeader>
                    <CardTitle className="text-xl font-serif text-primary">
                      {language === 'it' ? 'Stato Acquisto' : 'Purchase Status'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      {hasAccess ? (
                        <>
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-green-700">
                              {language === 'it' ? 'Valutazione Completa Attiva' : 'Complete Assessment Active'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {language === 'it' ? 'Accesso a tutti i test' : 'Access to all tests'}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-amber-700">
                              {language === 'it' ? 'Nessun Acquisto' : 'No Purchase'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {language === 'it' ? 'Acquista per accedere ai test' : 'Purchase to access tests'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {!hasAccess && (
                      <Button
                        className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 mt-4"
                        onClick={() => navigate('/pricing')}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {language === 'it' ? 'Acquista Ora - €199' : 'Buy Now - €199'}
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-medium rounded-3xl md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-xl font-serif text-primary">
                      {language === 'it' ? 'Appuntamenti' : 'Appointments'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={() => navigate('/appointments')}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {language === 'it' ? 'Gestisci Appuntamenti' : 'Manage Appointments'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DashboardPage;
